import { useMemo, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { civilToday, formatCivilDate, formatInstant } from '@eis/core';
import { catalogLabel } from '@eis/patterns';
import { useFilterCatalog, usePolicySnapshots } from '../api/policies';
import { timelineEntries } from '../model/timeline';
import { PolicyTimeline } from './PolicyTimeline';

/** What the cut bar changes in the URL: the business date, the system time — or clears them. */
export interface PolicyCutChange {
  readonly validAt?: string;
  readonly asOf?: string;
}

/**
 * Which belief of the policy the page reads (SDD-017 §6 "Console URL contract").
 *
 *  - The TIMELINE (PolicyTimeline): the periods of `GET /v1/policies/{code}/snapshots` left to
 *    right, the one the page shows pressed, what begins or changes at each. Choosing another
 *    writes its first day as `validAt` to the URL — the page then reads that period, and the link
 *    can be shared.
 *  - The "erfasst am" AUDIT toggle: reads the policy as it was RECORDED at an earlier instant
 *    (`asOf`). The instants offered are the moments the record changed — every slice's
 *    `recordedAt`, superseded beliefs included — because between two of them nothing differs.
 *    Off means "now".
 */
export function PolicyCutBar({
  code,
  shownValidFrom,
  asOf,
  locale,
  onChange,
}: {
  readonly code: string;
  /** The first day of the slice the page shows. */
  readonly shownValidFrom: string | undefined;
  readonly asOf: string | undefined;
  readonly locale: string;
  readonly onChange: (change: PolicyCutChange) => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const slices = usePolicySnapshots(code, asOf);
  const entries = useMemo(() => timelineEntries(slices.data ?? []), [slices.data]);
  // The changed fields are named as the policy's tarif names them: its filter catalog's labels.
  const tarifSlug = (slices.data?.[0]?.tarifVersionErn ?? '').split(':')[1]?.split('/')[0] ?? '';
  const catalog = useFilterCatalog('policies', { tarifSlug, enabled: tarifSlug !== '' });
  const fieldLabel = (key: string): string => {
    const field = catalog.data?.fields?.find((entry) => entry.key === key);
    return field === undefined ? (key.split('.').pop() ?? key) : catalogLabel(i18n, field, key);
  };
  // The audit view's moments include the beliefs a correction replaced — a read that needs the
  // history scope. A caller without it is refused (403); the moments then come from the current
  // beliefs, which every reader of the policy may see, and the page shows no error for it.
  const history = usePolicySnapshots(code, undefined, true);
  const moments = history.isError ? slices.data : history.data;

  // The moments the record changed, newest first, each with what was recorded then ("Änderung
  // ab 01.07.2026") — the first slice written at that instant names it.
  const instants = useMemo(() => {
    const byInstant = new Map<string, { operation: string; validFrom: string }>();
    for (const slice of moments ?? []) {
      const at = slice.recordedAt ?? '';
      if (at !== '' && !byInstant.has(at))
        byInstant.set(at, { operation: slice.operation ?? '', validFrom: slice.validFrom ?? '' });
    }
    return [...byInstant.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([at, what]) => ({ at, ...what }));
  }, [moments]);
  const instantValues = instants.map((instant) => instant.at);

  if (slices.isPending) return <Skeleton variant="rounded" height={88} />;

  return (
    <Stack spacing={1.5}>
      <PolicyTimeline
        entries={entries}
        selectedValidFrom={shownValidFrom}
        today={civilToday()}
        locale={locale}
        fieldLabel={fieldLabel}
        onSelect={(validFrom) => {
          onChange({ validAt: validFrom, ...(asOf === undefined ? {} : { asOf }) });
        }}
      />
      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={asOf !== undefined}
              disabled={instants.length === 0}
              onChange={(event) => {
                if (event.target.checked)
                  onChange({
                    ...(shownValidFrom === undefined ? {} : { validAt: shownValidFrom }),
                    asOf: instants[0]?.at ?? '',
                  });
                else
                  onChange({
                    ...(shownValidFrom === undefined ? {} : { validAt: shownValidFrom }),
                  });
              }}
            />
          }
          label={t('policies.cut.auditToggle')}
          aria-describedby="policy-audit-hint"
        />
        {asOf !== undefined && (
          <TextField
            select
            size="small"
            label={t('policies.cut.recordedAt')}
            value={instantValues.includes(asOf) ? asOf : ''}
            onChange={(event) => {
              onChange({
                ...(shownValidFrom === undefined ? {} : { validAt: shownValidFrom }),
                asOf: event.target.value,
              });
            }}
            sx={{ minWidth: 320 }}
          >
            {instants.map((instant) => (
              <MenuItem key={instant.at} value={instant.at}>
                {t('policies.cut.moment', {
                  time: formatInstant(instant.at, locale, undefined, { precise: true }),
                  operation: t(`policies.cut.operation.${instant.operation}` as never, {
                    defaultValue: instant.operation,
                  }),
                  from: formatCivilDate(instant.validFrom, locale),
                })}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" id="policy-audit-hint" sx={{ mt: -1 }}>
        {t('policies.cut.auditHint')}
      </Typography>
      {asOf !== undefined && (
        <Alert severity="info" data-testid="audit-read">
          {t('policies.cut.auditNote', {
            time: formatInstant(asOf, locale, undefined, { precise: true }),
          })}
        </Alert>
      )}
    </Stack>
  );
}
